import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast, { Toaster } from 'react-hot-toast';

import {
  downloadInvoiceXlsx,
  downloadInvoiceZip,
  getInvoiceDashboard,
  getPaymentRuns,
  updateInvoiceStatus,
} from '../../api_handlers/invoice';
import {
  Alert,
  BrandMark,
  Empty,
  ICONS,
  Icon,
  KIT_CSS,
  Modal,
  STATUS_META,
  StatusPill,
  StatusTrack,
  money,
  relative,
  shortDate,
  useStyleSheet,
} from './kit';
import InvoiceViewer from './InvoiceViewer';
import ExceptionReport, { REPORT_CSS } from './ExceptionReport';
import NoticeStrip, { NOTICE_CSS } from './NoticeStrip';
import PaymentRun, { RUN_CSS } from './PaymentRun';
import SpendAcrossCampaigns, { SPEND_CSS } from './SpendAcrossCampaigns';
import { AUDIENCE } from './lib/notifications';
import { campaignsFromInvoices, spendCoverage } from './lib/spendRollup';
import { commonNextStatuses, summariseBulk, toggleId } from './lib/bulkStatus';
import { filtersFromSearch } from './lib/consoleFilters';
import { invoiceFlags, reviewOf } from './lib/invoiceChecks';
import {
  STATUS_WATCH_KEY,
  changedIds,
  mergeSnapshot,
  readSnapshot,
  writeSnapshot,
} from './lib/statusWatch';
import { fetchAllRows } from './lib/pagedFetch';

const PAGE_SIZE = 25;
// The spend panel totals every campaign it is given, so handing it one page
// of PAGE_SIZE produced a figure that was wrong rather than merely small --
// and it sat on the same screen as the server's own correct total. The panel
// asks for this much per request and keeps drawing pages until it has the
// whole filtered set; where it still falls short, it says so rather than
// implying a whole.
const SPEND_PAGE_SIZE = 500;

// How many post links fit in a table cell before the cell stops being a
// shortcut; the rest move behind "+N".
const POSTS_IN_ROW = 3;

const PLATFORM_LABEL = { instagram: 'Instagram', youtube: 'YouTube', other: 'Link' };

/**
 * The planner page for a creator. `/planner/profile-details` resolves the
 * handle to a profile itself, so this works even for creators the invoice
 * console has never seen indexed.
 */
function plannerHref(profile) {
  return `/planner/profile-details/${profile.platform}/${encodeURIComponent(
    profile.handle,
  )}?src=invoice_console`;
}

/** Instagram first — it is the handle staff recognise. */
function primaryProfile(profiles) {
  const list = profiles || [];
  return list.find((each) => each.platform === 'instagram') || list[0] || null;
}

const TABS = [
  { value: '', label: 'All' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'approved', label: 'Approved' },
  { value: 'sent_for_payment', label: 'Sent for payment' },
  { value: 'paid', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' },
];

const CSS = `
.ivs-page { background: #F2F4F7; }

/* --- console header ----------------------------------------------- */
.ivs-top {
  background: linear-gradient(135deg, #0B0D12 0%, #141922 55%, #10303A 100%);
  border-radius: 18px; padding: 22px 26px; color: #fff; margin-bottom: 18px;
  display: flex; align-items: flex-start; justify-content: space-between; gap: 22px; flex-wrap: wrap;
}
.ivs-top__title { font-size: 22px; font-weight: 800; letter-spacing: -.02em; margin: 0; color: #fff; }
.ivs-top__sub { font-size: 13.5px; color: rgba(255,255,255,.6); margin: 6px 0 0; }
.ivs-top__actions { display: flex; gap: 9px; flex-wrap: wrap; }
.ivs-top .iv-btn--ghost { background: rgba(255,255,255,.08); border-color: rgba(255,255,255,.18); color: #fff; }
.ivs-top .iv-btn--ghost:hover:not(:disabled) { background: rgba(255,255,255,.16); border-color: rgba(255,255,255,.35); color: #fff; }

/* --- ledger strip -------------------------------------------------- */
.ivs-ledger { display: grid; grid-template-columns: repeat(5, 1fr); background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); overflow: hidden; margin-bottom: 18px; }
.ivs-ledger__cell { padding: 16px 20px; border-right: 1px solid var(--line); }
.ivs-ledger__cell:last-child { border-right: 0; }
.ivs-ledger__label { font-size: 11px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; color: var(--ink-4); margin: 0 0 6px; }
.ivs-ledger__value { font-size: 21px; font-weight: 800; letter-spacing: -.02em; color: var(--ink); line-height: 1; }
.ivs-ledger__value--accent { color: var(--accent-ink); }
.ivs-ledger__value--warn { color: #B45309; }
.ivs-ledger__sub { font-size: 11.5px; font-weight: 700; color: #B91C1C; margin-top: 5px; }

/* --- tabs + filters ------------------------------------------------ */
.ivs-tabs { display: flex; gap: 4px; overflow-x: auto; padding-bottom: 2px; margin-bottom: 12px; scrollbar-width: none; }
.ivs-tabs::-webkit-scrollbar { display: none; }
.ivs-tab {
  border: 1px solid transparent; background: transparent; cursor: pointer; font-family: inherit;
  font-size: 13px; font-weight: 700; color: var(--ink-3); padding: 7px 13px; border-radius: 999px;
  display: inline-flex; align-items: center; gap: 7px; white-space: nowrap;
  transition: background .14s ease, color .14s ease, border-color .14s ease;
}
.ivs-tab:hover { background: #E9ECF2; color: var(--ink); }
.ivs-tab--on { background: var(--ink); color: #fff; border-color: var(--ink); }
.ivs-tab--on:hover { background: var(--ink); color: #fff; }
.ivs-tab__n { font-size: 11px; font-weight: 800; border-radius: 999px; padding: 1px 6px; background: rgba(0,0,0,.07); color: inherit; }
.ivs-tab--on .ivs-tab__n { background: rgba(255,255,255,.2); }

.ivs-filters { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-bottom: 14px; }
.ivs-filters__search { flex: 1 1 320px; min-width: 220px; max-width: 460px; }
.ivs-filters__dates { display: flex; align-items: center; gap: 7px; font-size: 12.5px; color: var(--ink-3); }
.ivs-filters__dates .iv-input { padding: 8px 10px; font-size: 13px; width: 148px; }
.ivs-filters__campaign { width: auto; max-width: 260px; padding: 9px 10px; font-size: 13px; }

/* --- table --------------------------------------------------------- */
.ivs-tableWrap { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); overflow: hidden; }
.ivs-scroll { overflow-x: auto; }
.ivs-table { width: 100%; border-collapse: collapse; min-width: 1040px; }
.ivs-table thead th {
  position: sticky; top: 0; z-index: 2; background: #FAFBFC; text-align: left;
  font-size: 11px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; color: var(--ink-4);
  padding: 11px 16px; border-bottom: 1px solid var(--line); white-space: nowrap;
}
.ivs-table thead th.ivs-r { text-align: right; }
.ivs-table tbody td { padding: 13px 16px; border-bottom: 1px solid #F1F3F7; vertical-align: middle; font-size: 13.5px; color: var(--ink-2); }
.ivs-table tbody tr:last-child td { border-bottom: 0; }
.ivs-table tbody tr { transition: background .12s ease; }
.ivs-table tbody tr:hover { background: #FAFCFD; }
.ivs-r { text-align: right; }

.ivs-who { display: flex; align-items: center; gap: 10px; min-width: 0; }
.ivs-who__name { font-weight: 800; color: var(--ink); font-size: 13.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ivs-who__sub { font-size: 11.5px; color: var(--ink-4); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ivs-camp { font-weight: 700; color: var(--ink); }
.ivs-sub { font-size: 11.5px; color: var(--ink-4); margin-top: 2px; }

/* Tracing a line item back to its creator and its content. Both are links
   out of the console, so they are styled as links rather than buttons. */
.ivs-who__name a { color: inherit; text-decoration: none; border-bottom: 1px solid transparent; }
.ivs-who__name a:hover { color: var(--accent-ink); border-bottom-color: currentColor; }
.ivs-plats { display: flex; gap: 5px; margin-top: 4px; flex-wrap: wrap; }
.ivs-plat {
  font-size: 10.5px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase;
  border: 1px solid var(--line); border-radius: 5px; padding: 1px 5px; color: var(--ink-4);
  text-decoration: none; background: var(--surface);
}
.ivs-plat:hover { color: var(--ink); border-color: var(--line-2); }

.ivs-posts { display: flex; gap: 5px; margin-top: 6px; flex-wrap: wrap; align-items: center; }
.ivs-post {
  display: inline-flex; align-items: center; gap: 5px; max-width: 148px;
  font-family: inherit; font-size: 11.5px; font-weight: 700; color: var(--ink-3);
  background: var(--surface); border: 1px solid var(--line); border-radius: 6px;
  padding: 2px 7px; text-decoration: none; cursor: pointer;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ivs-post:hover { color: var(--ink); border-color: var(--line-2); background: #FAFCFD; }
.ivs-post__dot { width: 6px; height: 6px; border-radius: 50%; flex: none; }
.ivs-post__dot--instagram { background: #C13584; }
.ivs-post__dot--youtube { background: #FF0000; }
.ivs-post__dot--other { background: #9CA3AF; }
.ivs-exc { display: inline-block; margin-left: 8px; font-size: 10px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #92400E; background: #FEF3C7; border: 1px solid #FDE68A; border-radius: 999px; padding: 1px 7px; vertical-align: middle; cursor: help; }
.ivs-pay { margin-top: 4px; }
.ivs-unver { display: inline-block; margin-left: 6px; font-size: 10px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: #B91C1C; background: #FEE2E2; border-radius: 999px; padding: 0 6px; cursor: help; }
.ivs-dl { display: flex; flex-direction: column; gap: 8px; }
.ivs-dl__row { display: flex; align-items: center; justify-content: space-between; gap: 12px; border: 1px solid var(--line); border-radius: var(--radius-sm); padding: 10px 12px; }
.ivs-dl__name { font-weight: 700; color: var(--ink); }
.ivs-dl__meta { font-size: 11.5px; color: var(--ink-4); margin-top: 1px; }
.ivs-dl__actions { display: flex; gap: 6px; flex: none; }
.ivs-pay--missing { color: #B45309; }
.ivs-msme { display: inline-block; margin-left: 6px; font-size: 10px; font-weight: 800; color: #15803D; background: #DCFCE7; border-radius: 999px; padding: 0 6px; }
.ivs-filters__small { max-width: 170px; }
.ivs-moved { display: inline-block; margin-left: 7px; font-size: 10px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: #1D4ED8; background: #DBEAFE; border-radius: 999px; padding: 2px 7px; vertical-align: middle; }
.ivs-check { width: 36px; }
.ivs-check input { width: 16px; height: 16px; cursor: pointer; accent-color: #111936; }
.ivs-row--picked { background: #f5f7ff; }
.ivs-bulk {
  /* Pinned to the top of the scroll, below the 64px app bar: the selection is
     made from the header checkbox and the list runs long, so the action has to
     stay where the eye already is rather than at the far end of the table. */
  position: sticky; top: 76px; z-index: 20;
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
  margin: 0 0 14px; padding: 11px 14px;
  border: 1px solid #111936; border-radius: 12px; background: #111936; color: #fff;
  box-shadow: 0 10px 24px rgba(16,24,40,.18);
}
.ivs-bulk__count { font-size: 13px; font-weight: 700; margin-right: 2px; }
.ivs-bulk__spacer { flex: 1 1 auto; }
.ivs-bulk__btn {
  display: inline-flex; align-items: center; gap: 6px;
  min-height: 34px; padding: 0 14px; border-radius: 8px; cursor: pointer;
  border: 1px solid rgba(255,255,255,.35); background: transparent; color: #fff;
  font: inherit; font-size: 12.5px; font-weight: 700;
  transition: background-color 180ms ease;
}
.ivs-bulk__btn:hover:not(:disabled) { background: rgba(255,255,255,.14); }
.ivs-bulk__btn:disabled { opacity: .55; cursor: not-allowed; }
.ivs-bulk__btn:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
.ivs-bulk__none { font-size: 12.5px; color: rgba(255,255,255,.75); }
@media (max-width: 599px) { .ivs-bulk { top: 8px; } .ivs-bulk__btn { min-height: 44px; } }
@media (prefers-reduced-motion: reduce) { .ivs-bulk__btn { transition: none; } }

.ivs-moved__bar { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-bottom: 12px; padding: 9px 12px; border: 1px solid #BFDBFE; background: #EFF6FF; border-radius: var(--radius-sm); font-size: 12.5px; color: #1E3A8A; }
/* The link the creator typed in themselves, as opposed to one we matched. */
.ivs-post--creator { border-style: dashed; }
.ivs-post__label { overflow: hidden; text-overflow: ellipsis; }
.ivs-post--more { color: var(--accent-ink); font-weight: 800; }

.ivs-postList { display: flex; flex-direction: column; gap: 8px; }
.ivs-postList__row {
  display: flex; align-items: center; gap: 10px; text-decoration: none;
  border: 1px solid var(--line); border-radius: var(--radius-sm); padding: 10px 12px;
  color: var(--ink-2);
}
.ivs-postList__row:hover { border-color: var(--line-2); background: #FAFCFD; }
.ivs-postList__label { font-weight: 700; color: var(--ink); }
.ivs-postList__meta { font-size: 11.5px; color: var(--ink-4); margin-top: 1px; }
.ivs-amt { font-weight: 800; color: var(--ink); font-size: 14.5px; }
.ivs-amt--muted { font-weight: 700; color: var(--ink-4); font-size: 13px; }
.ivs-flag {
  display: inline-block; margin-top: 4px; font-size: 10.5px; font-weight: 800;
  letter-spacing: .03em; color: #92400E; background: #FEF3C7; border: 1px solid #FDE68A;
  border-radius: 999px; padding: 1px 8px; cursor: help;
}
.ivs-rev { list-style: none; margin: 0 0 14px; padding: 0; display: grid; gap: 10px; }
.ivs-rev__row { border: 1px solid var(--line); border-radius: var(--radius-sm); padding: 10px 12px; display: grid; gap: 6px; }
.ivs-rev__who { font-size: 13.5px; font-weight: 800; color: var(--ink); }
.ivs-rev__flag { font-size: 12.5px; line-height: 1.5; display: grid; gap: 1px; border-left: 2px solid #FDE68A; padding-left: 9px; color: var(--ink-2); }
.ivs-rev__flag--info { border-left-color: var(--line-2); color: var(--ink-3); }
.ivs-rev__label { font-size: 10.5px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: #92400E; }
.ivs-rev__flag--info .ivs-rev__label { color: var(--ink-3); }
.ivs-gap { font-size: 11px; font-weight: 800; margin-top: 2px; }
.ivs-gap--over { color: #B45309; }
.ivs-gap--under { color: #15803D; }

/* Hover-to-reveal hides the affordance from a team that lives in this
   table all day, so the controls always show and only gain colour. */
.ivs-actions { display: flex; gap: 4px; justify-content: flex-end; }
.ivs-icon {
  border: 1px solid var(--line); background: var(--surface); color: var(--ink-4); cursor: pointer;
  width: 30px; height: 30px; border-radius: 8px; display: grid; place-items: center; padding: 0;
  transition: color .14s ease, border-color .14s ease, background .14s ease;
}
.ivs-table tbody tr:hover .ivs-icon { color: var(--ink-2); border-color: var(--line-2); }
.ivs-icon:hover { color: var(--accent-ink); border-color: var(--accent); background: var(--accent-bg); }
.ivs-icon:focus-visible { outline: 3px solid rgba(30,156,178,.4); outline-offset: 1px; }

/* --- transition menu ----------------------------------------------- */
.ivs-menu { position: fixed; z-index: 1400; background: var(--surface); border: 1px solid var(--line); border-radius: 12px; box-shadow: var(--shadow-lg); padding: 6px; min-width: 208px; }
.ivs-menu__head { font-size: 10.5px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; color: var(--ink-4); padding: 7px 10px 6px; margin: 0; }
.ivs-menu__head--split { border-top: 1px solid var(--line); margin-top: 5px; padding-top: 10px; }
.ivs-menu__rule { display: block; height: 1px; background: var(--line); margin: 5px 0; }
.ivs-menu__item {
  display: flex; align-items: center; gap: 9px; width: 100%; border: 0; background: none;
  font-family: inherit; font-size: 13.5px; font-weight: 700; color: var(--ink-2); cursor: pointer;
  padding: 9px 10px; border-radius: 8px; text-align: left;
}
.ivs-menu__item:hover { background: var(--canvas); color: var(--ink); }
.ivs-menu__item--danger { color: #B91C1C; }
.ivs-menu__item--danger:hover { background: #FEF2F2; }

/* --- pagination ---------------------------------------------------- */
.ivs-pager { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 13px 16px; border-top: 1px solid var(--line); background: #FAFBFC; flex-wrap: wrap; }
.ivs-pager__text { font-size: 12.5px; color: var(--ink-3); }
.ivs-pager__text strong { color: var(--ink); font-weight: 800; }
.ivs-pager__nav { display: flex; gap: 7px; }

/* --- history ------------------------------------------------------- */
.ivs-event { display: flex; gap: 12px; padding-bottom: 18px; position: relative; }
.ivs-event:last-child { padding-bottom: 0; }
.ivs-event::before { content: ''; position: absolute; left: 5px; top: 16px; bottom: 0; width: 2px; background: var(--line); }
.ivs-event:last-child::before { display: none; }
.ivs-event__dot { width: 12px; height: 12px; border-radius: 50%; background: var(--accent); flex: none; margin-top: 3px; position: relative; z-index: 1; box-shadow: 0 0 0 3px var(--accent-bg); }
.ivs-event__title { font-size: 13.5px; font-weight: 800; color: var(--ink); margin: 0; }
.ivs-event__meta { font-size: 12px; color: var(--ink-4); margin: 3px 0 0; }
.ivs-event__note { font-size: 13px; color: var(--ink-2); margin: 7px 0 0; padding: 9px 11px; background: var(--canvas); border-radius: 8px; line-height: 1.5; }

.ivs-kv { display: grid; grid-template-columns: 132px 1fr; gap: 9px 16px; font-size: 13.5px; }
.ivs-kv dt { color: var(--ink-4); font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; padding-top: 2px; }
.ivs-kv dd { margin: 0; color: var(--ink); font-weight: 700; }

@media (max-width: 900px) {
  .ivs-ledger { grid-template-columns: repeat(2, 1fr); }
  .ivs-ledger__cell:nth-child(2) { border-right: 0; }
  .ivs-ledger__cell:nth-child(-n+2) { border-bottom: 1px solid var(--line); }
  .ivs-top { padding: 20px; }
}
`;

function Ledger({ summary, loading }) {
  const cells = [
    { label: 'Invoices', value: loading ? '—' : summary.invoice_count, plain: true },
    { label: 'Total raised', value: loading ? '—' : money(summary.total_amount) },
    { label: 'Paid out', value: loading ? '—' : money(summary.paid_amount), tone: 'accent' },
    {
      label: 'Outstanding',
      value: loading ? '—' : money(summary.outstanding_amount),
      tone: summary.outstanding_amount > 0 ? 'warn' : undefined,
    },
    {
      // The exception path is meant to stay rare; this is where anyone
      // would first notice it becoming the default.
      label: 'Via exception link',
      value: loading
        ? '—'
        : `${(summary.entry_path_counts || {}).exception || 0} · ${Math.round(
            (summary.exception_share || 0) * 100,
          )}%`,
      plain: true,
      tone: (summary.exception_share || 0) > 0.2 ? 'warn' : undefined,
      sub:
        !loading && summary.unmatched_exception_count
          ? `${summary.unmatched_exception_count} unverified submitter${
              summary.unmatched_exception_count === 1 ? '' : 's'
            }`
          : '',
    },
  ];
  return (
    <div className="ivs-ledger">
      {cells.map((cell) => (
        <div className="ivs-ledger__cell" key={cell.label}>
          <p className="ivs-ledger__label">{cell.label}</p>
          <div
            className={`ivs-ledger__value iv-num${
              cell.tone ? ` ivs-ledger__value--${cell.tone}` : ''
            }`}
          >
            {cell.value}
          </div>
          {cell.sub ? <div className="ivs-ledger__sub">{cell.sub}</div> : null}
        </div>
      ))}
    </div>
  );
}

function TableSkeleton() {
  return (
    <tbody>
      {[0, 1, 2, 3, 4, 5].map((row) => (
        <tr key={row}>
          <td className="ivs-check" />
          <td>
            <div className="ivs-who">
              <div className="iv-sk" style={{ width: 30, height: 30, borderRadius: 8 }} />
              <div style={{ flex: 1 }}>
                <div className="iv-sk" style={{ width: '65%', height: 12 }} />
                <div className="iv-sk" style={{ width: '45%', height: 10, marginTop: 6 }} />
              </div>
            </div>
          </td>
          <td>
            <div className="iv-sk" style={{ width: '75%', height: 12 }} />
          </td>
          <td>
            <div className="iv-sk" style={{ width: 70, height: 12 }} />
          </td>
          <td className="ivs-r">
            <div className="iv-sk" style={{ width: 72, height: 14, marginLeft: 'auto' }} />
          </td>
          <td>
            <div className="iv-sk" style={{ width: 84, height: 20, borderRadius: 999 }} />
          </td>
          <td>
            <div className="iv-sk" style={{ width: 80, height: 12 }} />
          </td>
          <td />
        </tr>
      ))}
    </tbody>
  );
}

/**
 * next_statuses arrives as [{value, label, direction}]. It was read as a
 * list of plain strings, so each entry was handed to StatusPill as an
 * object, fell through the STATUS_META lookup, and got rendered as a React
 * child -- which throws "Objects are not valid as a React child" and blanks
 * the page the moment the menu opens. Accept either shape.
 */
function normaliseStatuses(raw) {
  return (raw || []).map((entry) =>
    typeof entry === 'string'
      ? { value: entry, label: (STATUS_META[entry] || {}).label || entry, direction: 'forward' }
      : entry,
  );
}

/**
 * Popover of the transitions the API says this invoice can actually make.
 *
 * Rendered through a portal on document.body: this page also runs inside the
 * planner shell, whose scroll containers and stacking contexts would
 * otherwise clip it. Dismissal is a containment check on a real pointerdown
 * rather than a blanket document listener, and scrolling repositions the
 * menu instead of closing it -- a capture-phase scroll listener fired on
 * unrelated nested scrollers and shut the menu the instant it opened.
 */
function TransitionMenu({ menu, onPick, onClose }) {
  const panel = useRef(null);
  const [pos, setPos] = useState(null);
  const anchor = menu && menu.anchor;

  useLayoutEffect(() => {
    if (!anchor) {
      setPos(null);
      return undefined;
    }
    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const el = panel.current;
      const height = el ? el.offsetHeight : 150;
      const width = el ? el.offsetWidth : 208;
      // Flip above the trigger when there is no room beneath it.
      const below = rect.bottom + 6;
      const top =
        below + height > window.innerHeight - 8 ? Math.max(8, rect.top - 6 - height) : below;
      const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
      setPos({ top, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor]);

  useEffect(() => {
    if (!anchor) return undefined;
    const onDown = (event) => {
      if (panel.current && panel.current.contains(event.target)) return;
      // The trigger toggles itself; closing here would fight its onClick.
      if (anchor.contains(event.target)) return;
      onClose();
    };
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  if (!menu) return null;
  const next = normaliseStatuses(menu.invoice.next_statuses);
  const forward = next.filter((o) => o.direction !== 'back' && o.value !== 'rejected');
  const back = next.filter((o) => o.direction === 'back');
  const reject = next.find((o) => o.value === 'rejected');

  return createPortal(
    <div
      className="iv-root ivs-menu"
      ref={panel}
      style={{ top: pos ? pos.top : -9999, left: pos ? pos.left : -9999 }}
      role="menu"
      aria-label="Change invoice status"
    >
      {forward.length > 0 ? <p className="ivs-menu__head">Move to</p> : null}
      {forward.map((option) => (
        <button
          key={option.value}
          type="button"
          role="menuitem"
          className="ivs-menu__item"
          onClick={() => onPick(menu.invoice, option)}
        >
          <StatusPill status={option.value} label={option.label} />
        </button>
      ))}

      {back.length > 0 ? (
        <>
          <p className="ivs-menu__head ivs-menu__head--split">Correct back to</p>
          {back.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitem"
              className="ivs-menu__item"
              onClick={() => onPick(menu.invoice, option)}
            >
              <StatusPill status={option.value} label={option.label} />
            </button>
          ))}
        </>
      ) : null}

      {reject ? (
        <>
          <span className="ivs-menu__rule" />
          <button
            type="button"
            role="menuitem"
            className="ivs-menu__item ivs-menu__item--danger"
            onClick={() => onPick(menu.invoice, reject)}
          >
            <StatusPill status={reject.value} label={reject.label} />
          </button>
        </>
      ) : null}
    </div>,
    document.body,
  );
}

export default function InvoiceDashboard() {
  useStyleSheet('iv-kit', KIT_CSS);
  useStyleSheet('ivs-page', CSS);
  useStyleSheet('ivr-report', REPORT_CSS);
  useStyleSheet('nt-notices', NOTICE_CSS);
  useStyleSheet('pr-runs', RUN_CSS);
  useStyleSheet('sp-spend', SPEND_CSS);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [summary, setSummary] = useState({
    invoice_count: 0,
    total_amount: 0,
    paid_amount: 0,
    outstanding_amount: 0,
    status_counts: {},
    campaigns: [],
  });
  // The submission email links here with ?campaign_id=, and a finance notice
  // with ?status=approved, so each lands on the queue it names. The tabs are
  // what this console can display, so they are what a link is checked against.
  const [filters, setFilters] = useState(() => ({
    status: '',
    campaign_id: '',
    campaign_subtype: '',
    entry_path: '',
    submitter_matched: '',
    search: '',
    submitted_after: '',
    submitted_before: '',
    ...filtersFromSearch(
      typeof window === 'undefined' ? '' : window.location.search,
      TABS.map((tab) => tab.value),
    ),
  }));
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [exporting, setExporting] = useState('');
  const [menu, setMenu] = useState(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  const [viewing, setViewing] = useState(null);
  const [postsFor, setPostsFor] = useState(null);
  const [history, setHistory] = useState(null);
  const [reject, setReject] = useState(null);
  const [payRef, setPayRef] = useState(null);
  // A forward move carrying warnings pauses here first; a bulk move always
  // does, because a count alone never said which invoices were about to go.
  const [confirming, setConfirming] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [downloadsOpen, setDownloadsOpen] = useState(false);
  const [runOpen, setRunOpen] = useState(false);
  const [spendOpen, setSpendOpen] = useState(false);
  const [spendRows, setSpendRows] = useState(null);
  const [spendTotal, setSpendTotal] = useState(0);
  const [spendLoading, setSpendLoading] = useState(false);
  const [runs, setRuns] = useState([]);
  // Who may do what. Named once: the same three checks were being spelled out
  // inline wherever they were needed, which is how two of them drift apart.
  const roles = summary.viewer_roles || [];
  const canPlan = roles.some((role) => role === 'admin' || role === 'business');
  const canPay = roles.some((role) => role === 'admin' || role === 'finance');
  const seesCosts = canPlan || canPay;

  // What moved since this browser last looked. The creator side gets this from
  // the API (`has_unseen_update`); staff have no such field yet, so it is
  // derived here — see lib/statusWatch.
  // Batch selection. Cleared whenever the result set changes, so a filter
  // change can never leave invisible rows selected.
  const [picked, setPicked] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState('');
  const [seen, setSeen] = useState(() =>
    readSnapshot(typeof window === 'undefined' ? null : window.localStorage, STATUS_WATCH_KEY),
  );
  const searchBox = useRef(null);

  // The list is debounced so typing in search doesn't fire a request per key.
  const [query, setQuery] = useState('');
  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((f) => (f.search === query ? f : { ...f, search: query }));
      setPage(1);
    }, 280);
    return () => clearTimeout(id);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getInvoiceDashboard({ ...filters, page, page_size: PAGE_SIZE });
      setRows(data.results || []);
      setCount(typeof data.count === 'number' ? data.count : (data.results || []).length);
      if (data.summary) setSummary(data.summary);
    } catch (e) {
      setError(
        e.status === 403
          ? 'You need staff access to view the invoice console.'
          : e.message || 'Could not load invoices.',
      );
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  // A filter or page change replaces the rows; keeping a selection across
  // that would act on invoices the operator can no longer see.
  useEffect(() => {
    setPicked(new Set());
  }, [filters, page]);

  useEffect(() => {
    load();
  }, [load]);

  // The spend panel needs the whole filtered set, not the page behind it.
  // Whether one request is enough is the server's choice, not ours, so this
  // keeps drawing pages until the count is met. Falls back to the page on
  // failure, which is the old behaviour, and the coverage line then says what
  // it is showing.
  useEffect(() => {
    if (!spendOpen) return undefined;
    let cancelled = false;

    setSpendLoading(true);
    fetchAllRows(
      (page, pageSize) => getInvoiceDashboard({ ...filters, page, page_size: pageSize }),
      { pageSize: SPEND_PAGE_SIZE },
    )
      .then(({ rows: all, count }) => {
        if (cancelled) return;
        setSpendRows(all);
        setSpendTotal(count);
      })
      .catch(() => {
        if (!cancelled) setSpendRows(null);
      })
      .finally(() => {
        if (!cancelled) setSpendLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [spendOpen, filters]);

  // Finance's "the file went out but no reference came back" notice needs the
  // runs themselves, not just the invoices. The endpoint resolves to an empty
  // list while it is unbuilt, so an absent backend costs one quiet request and
  // shows nothing — the notice simply never fires.
  useEffect(() => {
    if (!canPay) return undefined;
    let cancelled = false;
    getPaymentRuns()
      .then((data) => {
        if (!cancelled) setRuns((data && data.results) || []);
      })
      .catch(() => {
        /* advisory only: no runs means no notice, never an error on screen */
      });
    return () => {
      cancelled = true;
    };
  }, [canPay]);

  // "/" jumps to search, the way every console the team already uses does.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey) return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      e.preventDefault();
      if (searchBox.current) searchBox.current.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const apply = async (invoice, status, extra = {}) => {
    setBusyId(invoice.id);
    setMenu(null);
    try {
      await updateInvoiceStatus(invoice.id, { status, ...extra });
      const moved = (STATUS_META[status] || {}).label || status;
      toast.success(`${invoice.campaign_name} → ${moved.toLowerCase()}.`);
      load();
    } catch (e) {
      toast.error(e.message || 'Could not update the invoice.');
    } finally {
      setBusyId(null);
    }
  };

  // What happens once a move is allowed to go ahead. Paying still asks for the
  // reference, so a warning shown first does not skip that step.
  const proceed = (invoice, status) => {
    if (status === 'paid') {
      setPayRef({ invoice, reference: '' });
      return;
    }
    apply(invoice, status);
  };

  const pick = (invoice, option) => {
    setMenu(null);
    const status = typeof option === 'string' ? option : option.value;
    // Rejecting is the right response to a problem, so it is never questioned.
    if (status === 'rejected') {
      setReject({ invoice, comment: '' });
      return;
    }
    const review = reviewOf([invoice], option);
    if (review.needsReview) {
      setConfirming({ option, rows: [invoice], review });
      return;
    }
    proceed(invoice, status);
  };

  // `campaignId` narrows the current filters to one campaign's set — the
  // per-campaign bulk download — without touching what the table shows.
  const runExport = async (kind, campaignId) => {
    const scope = campaignId ? { ...filters, campaign_id: campaignId } : filters;
    setExporting(campaignId ? `${kind}-${campaignId}` : kind);
    try {
      const result =
        kind === 'xlsx' ? await downloadInvoiceXlsx(scope) : await downloadInvoiceZip(scope);
      if (kind === 'zip' && result.skipped) {
        toast(`${result.included} files downloaded · ${result.skipped} had no readable document.`);
      }
    } catch (e) {
      toast.error(
        e.status === 404
          ? 'No invoice files match these filters.'
          : e.message || 'Could not build the download.',
      );
    } finally {
      setExporting('');
    }
  };

  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, count);
  const counts = summary.status_counts || {};

  // The campaign list comes back ignoring the campaign filter, but it still
  // honours status and dates — so a campaign can drop out from under a
  // selection. Keep the chosen id selectable rather than silently blank.
  const moved = changedIds(rows, seen);
  const movedSet = new Set(moved.map(String));

  // Remember the statuses now on screen, so the next visit compares against
  // them. Staff clear the highlight themselves — doing it on load would hide
  // the thing they came back to see.
  const acknowledge = () => {
    const next = mergeSnapshot(seen, rows);
    writeSnapshot(typeof window === 'undefined' ? null : window.localStorage, STATUS_WATCH_KEY, next);
    setSeen(next);
  };

  // --- batch actions -------------------------------------------------------
  const pickedRows = rows.filter((invoice) => picked.has(invoice.id));
  const bulkOptions = commonNextStatuses(pickedRows);
  const allPicked = rows.length > 0 && rows.every((invoice) => picked.has(invoice.id));

  const togglePick = (id) => setPicked((current) => toggleId(current, id));
  const toggleAll = () =>
    setPicked(allPicked ? new Set() : new Set(rows.map((invoice) => invoice.id)));

  // Money states. Say plainly what is about to happen, and to which invoices,
  // before it happens — the old confirm gave only a count.
  const runBulk = (option) => {
    setConfirming({ option, rows: pickedRows, review: reviewOf(pickedRows, option), bulk: true });
  };

  const applyBulk = async (option) => {
    setBulkBusy(option.value);
    // Sequential: the API takes one invoice at a time, and a burst of writes
    // against payment state is not worth the few seconds saved.
    const outcomes = [];
    for (let i = 0; i < pickedRows.length; i += 1) {
      const invoice = pickedRows[i];
      try {
        // eslint-disable-next-line no-await-in-loop
        await updateInvoiceStatus(invoice.id, { status: option.value });
        outcomes.push({ ok: true, name: invoice.influencer_name });
      } catch (e) {
        outcomes.push({ ok: false, name: invoice.influencer_name });
      }
    }
    const summaryLine = summariseBulk(outcomes, option.label);
    if (summaryLine.tone === 'success') toast.success(summaryLine.message);
    else if (summaryLine.tone === 'warn') toast(summaryLine.message, { icon: '⚠️' });
    else toast.error(summaryLine.message);

    setPicked(new Set());
    setBulkBusy('');
    load();
  };

  const campaignOptions = summary.campaigns || [];
  const campaignMissing =
    filters.campaign_id &&
    !campaignOptions.some((each) => String(each.id) === String(filters.campaign_id));

  return (
    <div className="iv-root iv-page ivs-page">
      <div className="iv-wrap iv-wrap--console">
        <header className="ivs-top">
          <div>
            <h1 className="ivs-top__title">Invoice console</h1>
            <p className="ivs-top__sub">
              Every invoice creators have raised, and the only place their status changes.
            </p>
          </div>
          <div className="ivs-top__actions">
            {/* Workspace is for admins and the business team; finance only
                works invoices, so they do not get the button. */}
            {canPlan ? (
              <a
                className="iv-btn iv-btn--primary"
                href={
                  filters.campaign_id
                    ? `/planner/invoices/workspace?campaign_id=${filters.campaign_id}`
                    : '/planner/invoices/workspace'
                }
              >
                <Icon path={ICONS.plus} size={15} />
                {filters.campaign_id ? 'Open in workspace' : 'Campaign workspace'}
              </a>
            ) : null}
            <button
              type="button"
              className="iv-btn iv-btn--ghost"
              disabled={Boolean(exporting)}
              onClick={() => runExport('xlsx')}
            >
              {exporting === 'xlsx' ? (
                <span className="iv-spin" />
              ) : (
                <Icon path={ICONS.sheet} size={15} />
              )}
              Spreadsheet
            </button>
            <button
              type="button"
              className="iv-btn iv-btn--ghost"
              disabled={Boolean(exporting)}
              onClick={() => runExport('zip')}
            >
              {exporting === 'zip' ? (
                <span className="iv-spin" />
              ) : (
                <Icon path={ICONS.download} size={15} />
              )}
              Invoice files
            </button>
            <button
              type="button"
              className="iv-btn iv-btn--ghost"
              onClick={() => setDownloadsOpen(true)}
            >
              <Icon path={ICONS.download} size={15} />
              By campaign
            </button>
            {/* Agreed internal cost and margin are business-and-finance
                figures (subsystem 1 §4), so the view that shows them is
                gated like the workspace. */}
            {seesCosts ? (
              <button
                type="button"
                className="iv-btn iv-btn--ghost"
                onClick={() => setSpendOpen(true)}
              >
                <Icon path={ICONS.sheet} size={15} />
                Spend
              </button>
            ) : null}
            {/* Finance accounts and pays; business approves (D7). Only the
                people who pay get the run. */}
            {canPay ? (
              <button
                type="button"
                className="iv-btn iv-btn--ghost"
                onClick={() => setRunOpen(true)}
              >
                <Icon path={ICONS.download} size={15} />
                Payment run
              </button>
            ) : null}
            <button
              type="button"
              className="iv-btn iv-btn--ghost"
              onClick={() => setReportOpen(true)}
            >
              <Icon path={ICONS.alert} size={15} />
              Exception usage
            </button>
          </div>
        </header>

        <NoticeStrip
          audience={roles.includes('finance') ? AUDIENCE.FINANCE : AUDIENCE.BUSINESS}
          context={{ invoices: rows, campaigns: campaignsFromInvoices(rows), runs }}
        />

        <Ledger summary={summary} loading={loading && rows.length === 0} />

        <div className="ivs-tabs" role="tablist">
          {TABS.map((tab) => {
            const n = tab.value ? counts[tab.value] : summary.invoice_count;
            return (
              <button
                key={tab.value || 'all'}
                type="button"
                role="tab"
                aria-selected={filters.status === tab.value}
                className={`ivs-tab${filters.status === tab.value ? ' ivs-tab--on' : ''}`}
                onClick={() => {
                  setFilters((f) => ({ ...f, status: tab.value }));
                  setPage(1);
                }}
              >
                {tab.label}
                {n ? <span className="ivs-tab__n iv-num">{n}</span> : null}
              </button>
            );
          })}
        </div>

        <div className="ivs-filters">
          <div className="ivs-filters__search iv-search">
            <span className="iv-search__icon">
              <Icon path={ICONS.search} size={15} />
            </span>
            <input
              ref={searchBox}
              className="iv-input"
              placeholder="Search creator, campaign or invoice number"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search invoices"
            />
            {query ? null : <span className="iv-search__kbd">/</span>}
          </div>
          <select
            className="iv-input ivs-filters__campaign"
            value={filters.campaign_id}
            onChange={(e) => {
              setFilters((f) => ({ ...f, campaign_id: e.target.value }));
              setPage(1);
            }}
            aria-label="Filter by campaign"
          >
            <option value="">All campaigns</option>
            {campaignMissing ? (
              <option value={filters.campaign_id}>Campaign #{filters.campaign_id}</option>
            ) : null}
            {campaignOptions.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name} ({campaign.invoice_count})
              </option>
            ))}
          </select>
          <select
            className="iv-input ivs-filters__small"
            value={filters.campaign_subtype}
            onChange={(e) => {
              setFilters((f) => ({ ...f, campaign_subtype: e.target.value }));
              setPage(1);
            }}
            aria-label="Filter by campaign type"
          >
            <option value="">All types</option>
            <option value="paid">Paid</option>
            <option value="barter">Barter</option>
          </select>
          <select
            className="iv-input ivs-filters__small"
            value={filters.entry_path}
            onChange={(e) => {
              setFilters((f) => ({ ...f, entry_path: e.target.value }));
              setPage(1);
            }}
            aria-label="Filter by how the invoice came in"
          >
            <option value="">All entry paths</option>
            <option value="creator">Creator (signed in)</option>
            <option value="exception">Exception link</option>
          </select>
          <select
            className="iv-input ivs-filters__small"
            value={filters.submitter_matched}
            onChange={(e) => {
              setFilters((f) => ({ ...f, submitter_matched: e.target.value }));
              setPage(1);
            }}
            aria-label="Filter by whether the submitter was verified"
          >
            <option value="">Any submitter</option>
            <option value="false">Unverified submitter</option>
            <option value="true">Verified submitter</option>
          </select>
          <div className="ivs-filters__dates">
            <span>Raised</span>
            <input
              type="date"
              className="iv-input"
              value={filters.submitted_after}
              max={filters.submitted_before || undefined}
              onChange={(e) => {
                setFilters((f) => ({ ...f, submitted_after: e.target.value }));
                setPage(1);
              }}
              aria-label="Raised on or after"
            />
            <span>to</span>
            <input
              type="date"
              className="iv-input"
              value={filters.submitted_before}
              min={filters.submitted_after || undefined}
              onChange={(e) => {
                setFilters((f) => ({ ...f, submitted_before: e.target.value }));
                setPage(1);
              }}
              aria-label="Raised on or before"
            />
          </div>
          {filters.search ||
          filters.submitted_after ||
          filters.submitted_before ||
          filters.campaign_id ||
          filters.campaign_subtype ||
          filters.entry_path ||
          filters.submitter_matched ||
          filters.status ? (
            <button
              type="button"
              className="iv-btn iv-btn--quiet"
              onClick={() => {
                setQuery('');
                setFilters({
                  status: '',
                  campaign_id: '',
                  campaign_subtype: '',
                  entry_path: '',
                  submitter_matched: '',
                  search: '',
                  submitted_after: '',
                  submitted_before: '',
                });
                setPage(1);
              }}
            >
              Clear filters
            </button>
          ) : null}
        </div>

        {!error && moved.length > 0 ? (
          <div className="ivs-moved__bar">
            <span>
              {moved.length} invoice{moved.length === 1 ? '' : 's'} changed since you last looked.
            </span>
            <button type="button" className="iv-btn iv-btn--ghost iv-btn--sm" onClick={acknowledge}>
              Mark as seen
            </button>
          </div>
        ) : null}

        {pickedRows.length > 0 ? (
          <div className="ivs-bulk" role="region" aria-label="Actions for selected invoices">
            <span className="ivs-bulk__count">
              {pickedRows.length} selected
            </span>
            {bulkOptions.length === 0 ? (
              <span className="ivs-bulk__none">
                These invoices are at different stages — no single move applies to all of them.
              </span>
            ) : (
              bulkOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className="ivs-bulk__btn"
                  disabled={Boolean(bulkBusy)}
                  onClick={() => runBulk(option)}
                >
                  {bulkBusy === option.value ? <span className="iv-spin" /> : null}
                  Move to {option.label}
                </button>
              ))
            )}
            <span className="ivs-bulk__spacer" />
            <button
              type="button"
              className="ivs-bulk__btn"
              onClick={() => setPicked(new Set())}
              disabled={Boolean(bulkBusy)}
            >
              Clear
            </button>
          </div>
        ) : null}

        {error ? (
          <Alert onRetry={load}>{error}</Alert>
        ) : (
          <div className="ivs-tableWrap">
            <div className="ivs-scroll">
              <table className="ivs-table">
                <thead>
                  <tr>
                    <th className="ivs-check">
                      <input
                        type="checkbox"
                        checked={allPicked}
                        onChange={toggleAll}
                        aria-label={allPicked ? 'Clear selection' : 'Select every invoice shown'}
                        disabled={rows.length === 0}
                      />
                    </th>
                    <th>Creator</th>
                    <th>Campaign</th>
                    <th>Invoice no.</th>
                    <th className="ivs-r">Amount</th>
                    <th>Status</th>
                    <th>Raised</th>
                    <th className="ivs-r">Actions</th>
                  </tr>
                </thead>
                {loading ? (
                  <TableSkeleton />
                ) : (
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={8}>
                          <Empty icon={ICONS.search} title="Nothing matches these filters">
                            Try a wider date range, or clear the filters to see every invoice
                            creators have raised.
                          </Empty>
                        </td>
                      </tr>
                    ) : (
                      rows.map((invoice) => {
                        const agreed = Number(invoice.agreed_amount) || 0;
                        const amount = Number(invoice.amount) || 0;
                        const gap = agreed > 0 ? amount - agreed : 0;
                        const flags = invoiceFlags(invoice);
                        const profiles = invoice.influencer_profiles || [];
                        const profile = primaryProfile(profiles);
                        const posts = invoice.campaign_posts || [];
                        const postTotal = invoice.campaign_post_count || posts.length;
                        return (
                          <tr
                            key={invoice.id}
                            className={picked.has(invoice.id) ? 'ivs-row--picked' : undefined}
                          >
                            <td className="ivs-check">
                              <input
                                type="checkbox"
                                checked={picked.has(invoice.id)}
                                onChange={() => togglePick(invoice.id)}
                                aria-label={`Select ${invoice.influencer_name}'s invoice`}
                              />
                            </td>
                            <td>
                              <div className="ivs-who">
                                <BrandMark name={invoice.influencer_name} small />
                                <div style={{ minWidth: 0 }}>
                                  <div className="ivs-who__name">
                                    {profile ? (
                                      <a
                                        href={plannerHref(profile)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={`Open ${invoice.influencer_name} in the planner`}
                                      >
                                        {invoice.influencer_name}
                                      </a>
                                    ) : (
                                      invoice.influencer_name
                                    )}
                                  </div>
                                  <div className="ivs-who__sub">
                                    {invoice.contact_email || invoice.influencer_email}
                                  </div>
                                  {profiles.length > 1 ? (
                                    <div className="ivs-plats">
                                      {profiles.map((each) => (
                                        <a
                                          key={`${each.platform}-${each.handle}`}
                                          className="ivs-plat"
                                          href={plannerHref(each)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          title={`${
                                            PLATFORM_LABEL[each.platform] || each.platform
                                          } · ${each.handle}`}
                                        >
                                          {PLATFORM_LABEL[each.platform] || each.platform}
                                        </a>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="ivs-camp">
                                {invoice.campaign_name}
                                {invoice.entry_path === 'exception' ? (
                                  <span
                                    className="ivs-exc"
                                    title={
                                      invoice.staff && invoice.staff.exception
                                        ? `${invoice.staff.exception.reason}${
                                            invoice.staff.exception.created_by
                                              ? ` — link by ${invoice.staff.exception.created_by}`
                                              : ''
                                          }`
                                        : 'Submitted through an exception link'
                                    }
                                  >
                                    Exception
                                  </span>
                                ) : null}
                              </div>
                              <div className="ivs-sub">
                                {invoice.brand_name ? `${invoice.brand_name} · ` : ''}#
                                {invoice.campaign_id}
                              </div>
                              {invoice.entry_path === 'exception' &&
                              invoice.staff &&
                              invoice.staff.submitter ? (
                                <div
                                  className="ivs-sub"
                                  title={[
                                    invoice.staff.submitter.email,
                                    invoice.staff.submitter.phone,
                                    invoice.staff.submitter.profile,
                                    invoice.staff.submitter.match_note,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                >
                                  By {invoice.staff.submitter.type_display.toLowerCase()} ·{' '}
                                  {invoice.staff.submitter.agency
                                    ? `${invoice.staff.submitter.agency} (${invoice.staff.submitter.name})`
                                    : invoice.staff.submitter.name}
                                  {invoice.staff.submitter.matched === false ? (
                                    <span
                                      className="ivs-unver"
                                      title={invoice.staff.submitter.match_note}
                                    >
                                      Unverified
                                    </span>
                                  ) : null}
                                </div>
                              ) : null}
                              {invoice.staff && invoice.staff.payout ? (
                                <div className="ivs-sub ivs-pay">
                                  Pay to {invoice.staff.payout.bank_name || 'bank'} ••
                                  {invoice.staff.payout.account_number_last4} ·{' '}
                                  {invoice.staff.payout.ifsc_code}
                                  {invoice.staff.payout.is_msme ? (
                                    <span className="ivs-msme">MSME</span>
                                  ) : null}
                                  {invoice.staff.payout.gst_number ? ' · GST' : ''}
                                </div>
                              ) : (
                                <div className="ivs-sub ivs-pay ivs-pay--missing">
                                  No payout details on this invoice
                                </div>
                              )}
                              {posts.length ? (
                                <div className="ivs-posts">
                                  {posts.slice(0, POSTS_IN_ROW).map((post) => (
                                    <a
                                      key={post.url}
                                      className={`ivs-post${
                                        post.source === 'creator' ? ' ivs-post--creator' : ''
                                      }`}
                                      href={post.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={`${PLATFORM_LABEL[post.platform] || post.platform} · ${
                                        post.label
                                      }`}
                                    >
                                      <span
                                        className={`ivs-post__dot ivs-post__dot--${post.platform}`}
                                      />
                                      <span className="ivs-post__label">{post.label}</span>
                                    </a>
                                  ))}
                                  {postTotal > POSTS_IN_ROW ? (
                                    <button
                                      type="button"
                                      className="ivs-post ivs-post--more"
                                      onClick={() => setPostsFor(invoice)}
                                    >
                                      +{postTotal - POSTS_IN_ROW} more
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}
                            </td>
                            <td className="iv-num">{invoice.invoice_number || '—'}</td>
                            <td className="ivs-r">
                              <div className="ivs-amt iv-num">{money(invoice.amount)}</div>
                              {invoice.staff && invoice.staff.client_billing ? (
                                <div
                                  className="ivs-sub iv-num"
                                  title="Client billing, and what is left after this invoice"
                                >
                                  billed {money(invoice.staff.client_billing)}
                                  {invoice.staff.margin !== null &&
                                  invoice.staff.margin !== undefined ? (
                                    <span
                                      className={`ivs-gap ivs-gap--${
                                        invoice.staff.margin < 0 ? 'over' : 'under'
                                      }`}
                                      style={{ marginLeft: 6 }}
                                    >
                                      {money(invoice.staff.margin)}
                                    </span>
                                  ) : null}
                                </div>
                              ) : null}
                              {agreed > 0 ? (
                                <div className="ivs-sub iv-num">
                                  agreed {money(agreed)}
                                  {gap !== 0 ? (
                                    <span
                                      className={`ivs-gap ivs-gap--${gap > 0 ? 'over' : 'under'}`}
                                      style={{ marginLeft: 6 }}
                                    >
                                      {gap > 0 ? '+' : ''}
                                      {money(gap)}
                                    </span>
                                  ) : null}
                                </div>
                              ) : null}
                            </td>
                            <td>
                              <StatusPill status={invoice.status} label={invoice.status_display} />
                              {flags
                                .filter((flag) => flag.severity === 'warn')
                                .map((flag) => (
                                  <div className="ivs-flag" key={flag.id} title={flag.detail}>
                                    {flag.label}
                                  </div>
                                ))}
                              {movedSet.has(String(invoice.id)) ? (
                                <span className="ivs-moved" title="Changed since you last looked">
                                  Moved
                                </span>
                              ) : null}
                              {invoice.status === 'rejected' && invoice.remarks ? (
                                <div className="ivs-sub" title={invoice.remarks}>
                                  reason noted
                                </div>
                              ) : null}
                            </td>
                            <td>
                              <div className="iv-num">{shortDate(invoice.submitted_at)}</div>
                              <div className="ivs-sub">{relative(invoice.submitted_at)}</div>
                            </td>
                            <td className="ivs-r">
                              {busyId === invoice.id ? (
                                <span
                                  className="iv-spin"
                                  style={{ color: 'var(--accent)', display: 'inline-block' }}
                                />
                              ) : (
                                <div className="ivs-actions">
                                  {invoice.invoice_url ? (
                                    <button
                                      type="button"
                                      className="ivs-icon"
                                      title="View invoice"
                                      aria-label="View invoice"
                                      onClick={() => setViewing(invoice)}
                                    >
                                      <Icon path={ICONS.eye} size={15} />
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    className="ivs-icon"
                                    title="Status history"
                                    aria-label="Status history"
                                    onClick={() => setHistory(invoice)}
                                  >
                                    <Icon path={ICONS.clock} size={15} />
                                  </button>
                                  {(invoice.next_statuses || []).length > 0 ? (
                                    <button
                                      type="button"
                                      className="ivs-icon"
                                      title="Change status"
                                      aria-label="Change status"
                                      aria-haspopup="menu"
                                      aria-expanded={Boolean(
                                        menu && menu.invoice.id === invoice.id,
                                      )}
                                      onClick={(e) => {
                                        const el = e.currentTarget;
                                        setMenu((open) =>
                                          open && open.invoice.id === invoice.id
                                            ? null
                                            : { invoice, anchor: el },
                                        );
                                      }}
                                    >
                                      <Icon path={ICONS.chevron} size={15} />
                                    </button>
                                  ) : null}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                )}
              </table>
            </div>

            {count > 0 ? (
              <div className="ivs-pager">
                <span className="ivs-pager__text">
                  Showing <strong className="iv-num">{from}</strong>–
                  <strong className="iv-num">{to}</strong> of{' '}
                  <strong className="iv-num">{count}</strong>
                </span>
                <div className="ivs-pager__nav">
                  <button
                    type="button"
                    className="iv-btn iv-btn--ghost iv-btn--sm"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span className="ivs-pager__text" style={{ alignSelf: 'center' }}>
                    Page <strong className="iv-num">{page}</strong> of{' '}
                    <strong className="iv-num">{pages}</strong>
                  </span>
                  <button
                    type="button"
                    className="iv-btn iv-btn--ghost iv-btn--sm"
                    disabled={page >= pages || loading}
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <TransitionMenu menu={menu} onPick={pick} onClose={closeMenu} />

      <Modal
        open={Boolean(confirming)}
        onClose={() => setConfirming(null)}
        size={confirming && confirming.review.flagged.length > 0 ? 'md' : 'sm'}
        title={
          confirming && confirming.review.needsReview
            ? 'Check these before moving'
            : `Move to ${confirming ? confirming.option.label : ''}?`
        }
        subtitle={
          confirming
            ? `${confirming.review.total} invoice${
                confirming.review.total === 1 ? '' : 's'
              } → ${confirming.option.label}`
            : ''
        }
        footer={
          <>
            <button
              type="button"
              className="iv-btn iv-btn--ghost"
              onClick={() => setConfirming(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="iv-btn iv-btn--primary"
              onClick={() => {
                const { option, rows, bulk } = confirming;
                setConfirming(null);
                if (bulk) applyBulk(option);
                else proceed(rows[0], option.value);
              }}
            >
              {confirming && confirming.review.needsReview
                ? 'Move anyway'
                : `Move to ${confirming ? confirming.option.label : ''}`}
            </button>
          </>
        }
      >
        {confirming && confirming.review.flagged.length > 0 ? (
          <ul className="ivs-rev">
            {confirming.review.flagged.map((each) => (
              <li className="ivs-rev__row" key={each.id}>
                <div className="ivs-rev__who">
                  {each.name}
                  {each.campaign ? <span className="ivs-sub"> · {each.campaign}</span> : null}
                </div>
                {each.flags.map((flag) => (
                  <div className={`ivs-rev__flag ivs-rev__flag--${flag.severity}`} key={flag.id}>
                    <span className="ivs-rev__label">{flag.label}</span>
                    <span>{flag.detail}</span>
                  </div>
                ))}
              </li>
            ))}
          </ul>
        ) : null}
        <p className="iv-hint">
          {confirming && confirming.review.needsReview
            ? 'Nothing is blocked — these are flagged so the call is made knowingly. Reject instead if an invoice needs correcting.'
            : 'Each invoice moves one at a time, and the creator sees the new status.'}
        </p>
      </Modal>

      <Modal
        open={Boolean(reject)}
        onClose={() => setReject(null)}
        size="sm"
        title="Reject this invoice"
        subtitle={
          reject ? `${reject.invoice.influencer_name} · ${reject.invoice.campaign_name}` : ''
        }
        footer={
          <>
            <button type="button" className="iv-btn iv-btn--ghost" onClick={() => setReject(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="iv-btn iv-btn--danger"
              disabled={!reject || !reject.comment.trim()}
              onClick={() => {
                const { invoice, comment } = reject;
                setReject(null);
                apply(invoice, 'rejected', { remarks: comment.trim() });
              }}
            >
              Reject invoice
            </button>
          </>
        }
      >
        <label className="iv-field">
          <span className="iv-label">Reason</span>
          <textarea
            className="iv-textarea"
            rows={4}
            autoFocus
            placeholder="e.g. GST number missing from the invoice document"
            value={reject ? reject.comment : ''}
            onChange={(e) => setReject((r) => ({ ...r, comment: e.target.value }))}
          />
        </label>
        <p className="iv-hint">
          The creator sees this word for word on their invoice, so say exactly what they need to
          fix. They can correct and resubmit against the same campaign.
        </p>
      </Modal>

      <Modal
        open={Boolean(payRef)}
        onClose={() => setPayRef(null)}
        size="sm"
        title="Mark as paid"
        subtitle={
          payRef ? `${payRef.invoice.influencer_name} · ${money(payRef.invoice.amount)}` : ''
        }
        footer={
          <>
            <button type="button" className="iv-btn iv-btn--ghost" onClick={() => setPayRef(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="iv-btn iv-btn--primary"
              onClick={() => {
                const { invoice, reference } = payRef;
                setPayRef(null);
                apply(
                  invoice,
                  'paid',
                  reference.trim() ? { payment_reference: reference.trim() } : {},
                );
              }}
            >
              Mark paid
            </button>
          </>
        }
      >
        <label className="iv-field">
          <span className="iv-label">Payment reference / UTR</span>
          <input
            className="iv-input"
            autoFocus
            placeholder="Optional, but it saves a lot of questions later"
            value={payRef ? payRef.reference : ''}
            onChange={(e) => setPayRef((p) => ({ ...p, reference: e.target.value }))}
          />
        </label>
        <p className="iv-hint">Shown to the creator alongside the paid status.</p>
      </Modal>

      <Modal
        open={Boolean(history)}
        onClose={() => setHistory(null)}
        title="Invoice history"
        subtitle={history ? `${history.influencer_name} · ${history.campaign_name}` : ''}
      >
        {history ? (
          <>
            <dl className="ivs-kv" style={{ marginBottom: 22 }}>
              <dt>Invoice no.</dt>
              <dd className="iv-num">{history.invoice_number || '—'}</dd>
              <dt>Amount</dt>
              <dd className="iv-num">{money(history.amount)}</dd>
              <dt>Agreed</dt>
              <dd className="iv-num">{money(history.agreed_amount)}</dd>
              <dt>Contact</dt>
              <dd style={{ fontWeight: 400 }}>
                {history.contact_phone}
                <br />
                {history.contact_email}
              </dd>
              {history.payment_reference ? (
                <>
                  <dt>Payment ref</dt>
                  <dd className="iv-num">{history.payment_reference}</dd>
                </>
              ) : null}
            </dl>

            <StatusTrack status={history.status} />

            <div style={{ marginTop: 26 }}>
              {(history.status_events || []).length === 0 ? (
                <p className="iv-hint">
                  No status changes yet — this invoice is still where the creator left it.
                </p>
              ) : (
                history.status_events.map((event, i) => (
                  <div className="ivs-event" key={event.id || i}>
                    <span className="ivs-event__dot" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className="ivs-event__title">
                        {(STATUS_META[event.from_status] || {}).label ||
                          event.from_status ||
                          'Raised'}
                        {' → '}
                        {(STATUS_META[event.to_status] || {}).label || event.to_status}
                      </p>
                      <p className="ivs-event__meta">
                        {event.changed_by_name ? `${event.changed_by_name} · ` : ''}
                        {shortDate(event.created_at)} · {relative(event.created_at)}
                      </p>
                      {event.comment ? <p className="ivs-event__note">{event.comment}</p> : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(postsFor)}
        onClose={() => setPostsFor(null)}
        title="Campaign content"
        subtitle={postsFor ? `${postsFor.influencer_name} · ${postsFor.campaign_name}` : ''}
      >
        {postsFor ? (
          <div className="ivs-postList">
            {(postsFor.campaign_posts || []).map((post) => (
              <a
                key={post.url}
                className="ivs-postList__row"
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className={`ivs-post__dot ivs-post__dot--${post.platform}`} />
                <span style={{ minWidth: 0 }}>
                  <div className="ivs-postList__label">{post.label}</div>
                  <div className="ivs-postList__meta">
                    {PLATFORM_LABEL[post.platform] || post.platform}
                    {post.posted_at ? ` · ${shortDate(post.posted_at)}` : ''}
                    {post.source === 'creator' ? ' · link given by the creator' : ''}
                  </div>
                </span>
              </a>
            ))}
          </div>
        ) : null}
      </Modal>

      <ExceptionReport
        open={reportOpen}
        filters={filters}
        onClose={() => setReportOpen(false)}
        onShowUnmatched={() => {
          setReportOpen(false);
          setFilters((f) => ({ ...f, entry_path: 'exception', submitter_matched: 'false' }));
          setPage(1);
        }}
        onShowCampaign={(campaignId) => {
          setReportOpen(false);
          setFilters((f) => ({ ...f, entry_path: 'exception', campaign_id: String(campaignId) }));
          setPage(1);
        }}
      />

      <Modal
        open={downloadsOpen}
        onClose={() => setDownloadsOpen(false)}
        title="Download by campaign"
        subtitle="One campaign's invoices per click, within the current filters."
      >
        {campaignOptions.length === 0 ? (
          <Empty icon={ICONS.search} title="No campaigns match these filters">
            Clear the filters to see every campaign with invoices.
          </Empty>
        ) : (
          <div className="ivs-dl">
            {campaignOptions.map((campaign) => (
              <div className="ivs-dl__row" key={campaign.id}>
                <div style={{ minWidth: 0 }}>
                  <div className="ivs-dl__name">{campaign.name}</div>
                  <div className="ivs-dl__meta iv-num">
                    #{campaign.id} · {campaign.invoice_count} invoice
                    {campaign.invoice_count === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="ivs-dl__actions">
                  <button
                    type="button"
                    className="iv-btn iv-btn--ghost iv-btn--sm"
                    disabled={Boolean(exporting)}
                    onClick={() => runExport('zip', campaign.id)}
                  >
                    {exporting === `zip-${campaign.id}` ? (
                      <span className="iv-spin" />
                    ) : (
                      <Icon path={ICONS.download} size={14} />
                    )}
                    Files
                  </button>
                  <button
                    type="button"
                    className="iv-btn iv-btn--ghost iv-btn--sm"
                    disabled={Boolean(exporting)}
                    onClick={() => runExport('xlsx', campaign.id)}
                  >
                    {exporting === `xlsx-${campaign.id}` ? (
                      <span className="iv-spin" />
                    ) : (
                      <Icon path={ICONS.sheet} size={14} />
                    )}
                    Sheet
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        open={runOpen}
        onClose={() => setRunOpen(false)}
        title="Payment run"
        subtitle="Approved invoices, paid together. The file goes to the bank; we do not move money."
        size="lg"
      >
        <PaymentRun
          invoices={rows}
          onCreated={(created) => {
            setRunOpen(false);
            // The invoices in it have moved on, and the run itself is now
            // waiting for its reference. Both are on screen behind this.
            setRuns((current) => (created ? [created, ...current] : current));
            load();
          }}
        />
      </Modal>

      <Modal
        open={spendOpen}
        onClose={() => setSpendOpen(false)}
        title="Spend across campaigns"
        subtitle="Committed spend counted from submission, so an overrun shows before it is paid."
        size="lg"
      >
        {spendLoading && !spendRows ? (
          <p className="iv-lede">Loading every invoice that matches these filters…</p>
        ) : (
          (() => {
            const source = spendRows || rows;
            return (
              <SpendAcrossCampaigns
                campaigns={campaignsFromInvoices(source)}
                invoices={source}
                coverage={spendCoverage(source.length, spendRows ? spendTotal : count)}
              />
            );
          })()
        )}
      </Modal>

      <InvoiceViewer
        open={Boolean(viewing)}
        invoice={viewing}
        onClose={() => setViewing(null)}
        canEditTax={canPay}
        onTaxSaved={(updated) => {
          // The panel stays open, so it has to show what was just saved —
          // otherwise finance sees no change and saves again.
          setViewing((current) => (current && updated ? { ...current, ...updated } : current));
          load();
        }}
      />
      <Toaster position="bottom-center" />
    </div>
  );
}
